import React from 'react'
import { usePetsittingMode } from '@/store/usePetsittingMode'
import { Box } from '@mui/material'
import NextImage from 'next/image'
import { SxProps } from '@mui/material/styles'
import { openUrlApp } from '@/utils'
import {
  CrossIcon,
  EmtChip,
  EmtDrawerLink,
  EmtIcon,
  EmtNav,
  EmtNavLink,
  EmtProfilBox,
  EmtSeparator,
  EmtTypography,
} from 'emt-components-v2'
import { Trans, useTranslation } from 'next-i18next'
import NavNextLink from './NavNextLink'
import NextALink from '../NextALink'
import { Routes, SpecificModules } from '@/assets/configs'
import { useEMTState } from '@/store'
import DrawerNextLink from './DrawerNextLink'
import { useIsQueryOwner, useLogout } from '@/hooks'
import colors from '@/assets/themes/palettes/colors'
import ProfileMenuPets from '@/components/EMT/ProfileMenuPets'

import VariantImages from '@/assets/VariantImages'
import { useRouter } from 'next/router'
import NextMuiLink from '../NextMuiLink'
import BecomePremiumButton from './BecomePremiumButton'
import CertifyButton from './CertifyButton'
import AddAnimalModal from '@/components/PetSitter/AddAnimal/AddAnimalModal'
import { useUIStore } from '@/store/ui/uiStore'
import useCurrentUser from '@/hooks/useCurrentUser'
import AppModeSelect from './AppModeSelect'
import { Constants } from '@/assets'

import PetsDropdown from '@/components/EMT/PetsDropdown'
import { TokenKeys } from '@/modules/Auth/LocalStorageHelper'

type NavigationProps = {
  sx?: SxProps
}
enum ActiveItem {
  Home = 'home',
  Search = 'search',
  Messages = 'messages',
  Favorites = 'favorites',
  // PetSitter = 'pet-sitter',
  PetSitterSearch = 'pet-sitter/search',
  PetSitterReservations = 'pet-sitter/reservations',
  PetSitterNewRequest = 'pet-sitter/new-request',
  PetSitterPayment = 'pet-sitter/payment',
}

export const Navigation = ({ sx, shouldTransform }: NavigationProps & { shouldTransform?: boolean }) => {
  if (Constants.brand === 'emt') {
    return <EmtBrandNavigation sx={sx} shouldTransform={shouldTransform} />
  }
  return <DefaultBrandNavigation sx={sx} />
}

const EmtBrandNavigation = ({ sx, shouldTransform }: NavigationProps & { shouldTransform?: boolean }) => {
  const { t } = useTranslation('common')
  const { isPetsittingMode } = usePetsittingMode()
  const [isQueryOwner] = useIsQueryOwner()

  const {
    closeDrawer,
    isDrawerOpen,
    toggleDrawer,
    closeProfileMenu,
    openProfileMenu,
    isProfileMenuOpen,
    isAddPetModalOpen,
    closeAddPetModal,
  } = useUIStore()
  const { onLogoutClickAsync } = useLogout()

  const {
    discussions: { unreadCount: discussionsUnreadCount },
    apiNotifications: { currentApiNotifications },
  } = useEMTState()
  const [, queryType, appendQueryType] = useIsQueryOwner(true)

  const { isPremium, isCertified, currentUser, tendersEnabled, hasRequestedPetsitting } = useCurrentUser()

  const pets = currentUser?.animals || []

  const messagesCount = discussionsUnreadCount
  const favoritesCount = currentApiNotifications.filter(
    (apiNotification) => apiNotification.status === 'unread' && apiNotification.context === 'favorite',
  ).length

  const router = useRouter()

  // TO DO: hide
  const { pathname } = router
  const isPetSitterPage = pathname.includes('/pet-sitter')
  const isBorrowerPage =
    pathname.includes('/search') || pathname.includes('/favorites') || pathname.includes('/message')

  let activeItem: ActiveItem | null = null
  switch (router.pathname) {
    case Routes.Home:
      activeItem = ActiveItem.Home
      break
    case Routes.Search._pathname:
      activeItem = ActiveItem.Search
      break
    case Routes.Message._pathname:
      activeItem = ActiveItem.Messages
      break
    case Routes.Favorites._pathname:
      activeItem = ActiveItem.Favorites
      break
    // case Routes.PetSitter.Base:
    //   activeItem = ActiveItem.PetSitter
    //   break
    case Routes.PetSitter.Search:
      activeItem = ActiveItem.PetSitterSearch
      break
    case Routes.PetSitter.Reservations.Base:
      activeItem = ActiveItem.PetSitterReservations
      break
    case Routes.PetSitter.Reservations.NewRequest:
      activeItem = ActiveItem.PetSitterNewRequest
      break
    case Routes.PetSitter.Reservations.Payment:
      activeItem = ActiveItem.PetSitterPayment
      break
    default:
      break
  }

  const profilMenuChildren = isQueryOwner ? (
    <ProfileMenuPets
      pets={currentUser?.animals}
      onClick={() => {
        closeProfileMenu()
      }}
    />
  ) : (
    <>
      <Box
        onClick={() => {
          closeProfileMenu()
        }}
      >
        <NavNextLink key="edit-profile" href={appendQueryType(Routes.MyProfile)}>
          <EmtIcon className="emt-edit-1" sx={{ fontWeight: 'bold' }} />
          <EmtTypography variant="body2" fontWeight="bold">
            {t('navigation.editProfile')}
          </EmtTypography>
        </NavNextLink>
      </Box>

      <EmtNavLink
        key="logout"
        onClick={() => {
          closeProfileMenu()
          onLogoutClickAsync()
        }}
      >
        <EmtIcon className="emt-logout" sx={{ fontWeight: 'bold' }} />
        <EmtTypography variant="body2" fontWeight="bold">
          {t('navigation.logout')}
        </EmtTypography>
      </EmtNavLink>
    </>
  )

  const drawerChildren = (
    <>
      {isQueryOwner ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box>
            <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <EmtTypography variant="h5" fontWeight="normal">
                <Trans t={t} i18nKey="navigation.hello" values={{ name: currentUser?.displayName }} />
              </EmtTypography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                {isPremium && (
                  <EmtChip
                    label={t('profileCard.premium') as string}
                    color="secondarylight"
                    size="medium"
                    icon={<EmtIcon className="emt-crown" />}
                  />
                )}
                {isCertified && (
                  <EmtChip label={t('profileCard.idChecked') as string} color="successlight" size="medium" />
                )}
              </Box>
            </Box>
            <EmtTypography variant="body2">
              {isQueryOwner ? t('navigation.upToDateOwner') : t('navigation.upToDateBorrower')}
            </EmtTypography>
          </Box>
          {currentUser && (
            <PetsDropdown
              profile={currentUser}
              onPetClick={() => {
                closeDrawer()
              }}
            />
          )}
        </Box>
      ) : (
        <Box onClick={closeDrawer}>
          <NextMuiLink href={appendQueryType(Routes.MyProfile)} sx={{ textDecoration: 'none' }}>
            <EmtProfilBox
              username={currentUser?.displayName}
              secondaryLine={t('navigation.myProfile') as string}
              avatarUrl={currentUser?.mainPictureThumbnailUrl}
            />
          </NextMuiLink>
        </Box>
      )}
      <Box onClick={closeDrawer}>
        <BecomePremiumButton />
      </Box>
      <DrawerNextLink
        iconClassName="emt-cog"
        title={t('navigation.drawer.settings')}
        href={appendQueryType(Routes.MyAccount.settings)}
      />
      {SpecificModules.drawer.emergencyPetCareRelay && SpecificModules.others.emergencyPetCareRelay && (
        <DrawerNextLink
          icon={<EmtIcon fontSize="large" className="emt-pet-care-relay" />}
          title={t('navigation.drawer.emergencyPetCareRelay')}
          href={appendQueryType(Routes.EmergencyPetCareRelay)}
        />
      )}
      <DrawerNextLink
        iconClassName="emt-envelope"
        title={t('navigation.drawer.contact')}
        href={appendQueryType(Routes.Contact)}
      />

      {(!isQueryOwner && isPremium) || queryType === 'pro' ? (
        <DrawerNextLink
          icon={
            <Box sx={{ marginRight: '7px' }}>
              <CrossIcon color={colors.primary.main} />
            </Box>
          }
          title={t('navigation.drawer.assistance')}
          href={appendQueryType(Routes.Assistance)}
        />
      ) : null}
      {SpecificModules.drawer.partners && queryType !== 'pro' && (
        <DrawerNextLink
          iconClassName="emt-gift"
          title={t('navigation.drawer.partners')}
          href={appendQueryType(Routes.Partners)}
        />
      )}
      <DrawerNextLink
        iconClassName="emt-lock"
        title={t('navigation.drawer.safety')}
        href={appendQueryType(Routes.Safety)}
      />
      <DrawerNextLink
        iconClassName="emt-question-circle"
        title={t('navigation.drawer.help.title')}
        href={isPetsittingMode ? t('navigation.drawer.help.petsitting.url') : t('navigation.drawer.help.url')}
      />

      {!isPetsittingMode && (
        <Box onClick={closeDrawer}>
          <CertifyButton />
        </Box>
      )}

      <EmtSeparator />

      <Box
        onClick={() => {
          closeDrawer()
          onLogoutClickAsync()
        }}
      >
        <EmtDrawerLink sx={{ '& .emt-right-1': { display: 'none' } }}>
          <EmtIcon className="emt-logout" color="primary" />
          <EmtTypography variant="body1" color="black">
            {t('navigation.logout')}
          </EmtTypography>
        </EmtDrawerLink>
      </Box>
    </>
  )

  const mobileChildren = isQueryOwner ? (
    <>
      {/* TO DO: hide */}
      {isBorrowerPage && !isPetsittingMode && hasRequestedPetsitting && (
        <Box
          sx={{
            height: '54px',
            width: '100%',
            backgroundColor: '#E01B3F',
            position: 'absolute',
            bottom: '100%',
            left: 0,
            borderRadius: '12px 12px 0 0',
            display: 'grid',
            alignItems: 'center',
            gridTemplateColumns: 'repeat(3, 1fr)',
          }}
        >
          <NavNextLink
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px 12px 0 0',
              backgroundColor: activeItem === ActiveItem.Search ? '#B61538' : 'none',
            }}
            href={appendQueryType(Routes.Search._pathname)}
          >
            <EmtIcon className="emt-search-1" sx={{ fontSize: '15px!important', color: 'white!important' }} />
            <EmtTypography
              variant="body2"
              color="white!important"
              fontSize="10px"
              fontWeight="600"
              sx={{ '& span, p': { color: 'white!important' } }}
            >
              {t('navigation.mobile.search')}
            </EmtTypography>
          </NavNextLink>
          <NavNextLink
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px 12px 0 0',
              backgroundColor: activeItem === ActiveItem.Messages ? '#B61538' : 'none',
            }}
            notification={!!messagesCount}
            href={appendQueryType(Routes.Message._pathname)}
          >
            <EmtIcon className="emt-message-1" sx={{ fontSize: '15px!important', color: 'white!important' }} />
            <EmtTypography
              variant="body2"
              color="white!important"
              fontSize="10px"
              fontWeight="600"
              sx={{ '& span, p': { color: 'white!important' } }}
            >
              {t('navigation.mobile.messages')}
            </EmtTypography>
          </NavNextLink>
          <NavNextLink
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px 12px 0 0',
              backgroundColor: activeItem === ActiveItem.Favorites ? '#B61538' : 'none',
            }}
            notification={!!favoritesCount}
            href={appendQueryType(Routes.Favorites._pathname, Routes.Favorites._params.Sent)}
          >
            <EmtIcon className="emt-heart-1" sx={{ fontSize: '15px!important', color: 'white!important' }} />
            <EmtTypography
              variant="body2"
              color="white!important"
              fontSize="10px"
              fontWeight="600"
              sx={{ '& span, p': { color: 'white!important' } }}
            >
              {t('navigation.mobile.favorites')}
            </EmtTypography>
          </NavNextLink>
        </Box>
      )}

      {/* TO DO: hide */}
      {isPetSitterPage && !isPetsittingMode && (
        <Box
          sx={{
            height: '54px',
            width: '100%',
            backgroundColor: '#AE59C6',
            position: 'absolute',
            bottom: '100%',
            left: 0,
            borderRadius: '12px 12px 0 0',
            display: 'grid',
            alignItems: 'center',
            gridTemplateColumns: 'repeat(3, 1fr)',
          }}
        >
          {!tendersEnabled && (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px 12px 0 0',
                backgroundColor: activeItem === ActiveItem.PetSitterSearch ? '#762D8A' : 'none',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.preventDefault()
                const url = `https://app.supersitters.app/pet-sitter/search?token=${encodeURIComponent(
                  localStorage.getItem(TokenKeys.Auth ?? '') || '',
                )}&refreshToken=${encodeURIComponent(
                  localStorage.getItem(TokenKeys.Refresh ?? '') || '',
                )}&type=dogowner`
                openUrlApp(url)
              }}
            >
              {/* <NavNextLink
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px 12px 0 0',
                backgroundColor: activeItem === ActiveItem.PetSitterSearch ? '#762D8A' : 'none',
              }}
              notification={!!messagesCount}
              href={appendQueryType(Routes.PetSitter.Search)}
            > */}
              <EmtIcon className="emt-search-1" sx={{ fontSize: '15px!important', color: 'white!important' }} />
              <EmtTypography
                variant="body2"
                color="white!important"
                fontSize="10px"
                fontWeight="600"
                sx={{ '& span, p': { color: 'white!important' } }}
              >
                {t('navigation.mobile.petsitter.search')}
              </EmtTypography>
              {/* </NavNextLink> */}
            </Box>
          )}
          {tendersEnabled && (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px 12px 0 0',
                backgroundColor: activeItem === ActiveItem.PetSitterNewRequest ? '#762D8A' : 'none',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                e.preventDefault()
                const url = `https://app.supersitters.app/pet-sitter/reservations/new-request?token=${encodeURIComponent(
                  localStorage.getItem(TokenKeys.Auth ?? '') || '',
                )}&refreshToken=${encodeURIComponent(
                  localStorage.getItem(TokenKeys.Refresh ?? '') || '',
                )}&type=dogowner`
                openUrlApp(url)
              }}
            >
              {/* <NavNextLink
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '12px 12px 0 0',
                backgroundColor: activeItem === ActiveItem.PetSitterNewRequest ? '#762D8A' : 'none',
              }}
              notification={!!messagesCount}
              href={appendQueryType(Routes.PetSitter.Reservations.NewRequest)}
            > */}
              <EmtIcon className="emt-add" sx={{ fontSize: '15px!important', color: 'white!important' }} />
              <EmtTypography
                variant="body2"
                color="white!important"
                fontSize="10px"
                fontWeight="600"
                sx={{ '& span, p': { color: 'white!important' } }}
              >
                {t('navigation.mobile.petsitter.newReservation')}
              </EmtTypography>
              {/* </NavNextLink> */}
            </Box>
          )}
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px 12px 0 0',
              backgroundColor: activeItem === ActiveItem.PetSitterReservations ? '#762D8A' : 'none',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.preventDefault()
              const url = `https://app.supersitters.app/pet-sitter/reservations?token=${encodeURIComponent(
                localStorage.getItem(TokenKeys.Auth ?? '') || '',
              )}&refreshToken=${encodeURIComponent(localStorage.getItem(TokenKeys.Refresh ?? '') || '')}&type=dogowner`
              openUrlApp(url)
            }}
          >
            {/* <NavNextLink
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px 12px 0 0',
              backgroundColor: activeItem === ActiveItem.PetSitterReservations ? '#762D8A' : 'none',
            }}
            notification={!!favoritesCount}
            href={appendQueryType(Routes.PetSitter.Reservations.Base)}
          > */}
            <EmtIcon className="emt-message-text-alt-1" sx={{ fontSize: '15px!important', color: 'white!important' }} />
            <EmtTypography
              variant="body2"
              color="white!important"
              fontSize="10px"
              fontWeight="600"
              sx={{ '& span, p': { color: 'white!important' } }}
            >
              {t('navigation.mobile.petsitter.reservations')}
            </EmtTypography>
            {/* </NavNextLink> */}
          </Box>
        </Box>
      )}

      <NavNextLink active={activeItem === ActiveItem.Home} href={appendQueryType(Routes.Home)}>
        <EmtIcon className="emt-home-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography
          variant="body2"
          color="greyGradient.light"
          fontWeight="600"
          sx={{
            '& *': {
              color: activeItem === ActiveItem.Home ? '#4A4754!important' : '#77757F!important',
            },
          }}
        >
          {t('navigation.mobile.home')}
        </EmtTypography>
      </NavNextLink>
      {!isPetsittingMode && !hasRequestedPetsitting && (
        <>
          <NavNextLink active={activeItem === ActiveItem.Search} href={appendQueryType(Routes.Search._pathname)}>
            <EmtIcon className="emt-search-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
            <EmtTypography
              variant="body2"
              color="greyGradient.light"
              fontWeight="600"
              sx={{
                '& *': {
                  color: activeItem === ActiveItem.Search ? '#4A4754!important' : '#77757F!important',
                },
              }}
            >
              {t('navigation.mobile.search')}
            </EmtTypography>
          </NavNextLink>
          <NavNextLink active={activeItem === ActiveItem.Messages} href={appendQueryType(Routes.Message._pathname)}>
            <EmtIcon className="emt-message-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
            <EmtTypography
              variant="body2"
              color="greyGradient.light"
              fontWeight="600"
              sx={{
                '& *': {
                  color: activeItem === ActiveItem.Messages ? '#4A4754!important' : '#77757F!important',
                },
              }}
            >
              {t('navigation.mobile.messages')}
            </EmtTypography>
          </NavNextLink>
          <NavNextLink active={activeItem === ActiveItem.Favorites} href={appendQueryType(Routes.Favorites._pathname)}>
            <EmtIcon className="emt-heart-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
            <EmtTypography
              variant="body2"
              color="greyGradient.light"
              fontWeight="600"
              sx={{
                '& *': {
                  color: activeItem === ActiveItem.Favorites ? '#4A4754!important' : '#77757F!important',
                },
              }}
            >
              {t('navigation.mobile.favorites')}
            </EmtTypography>
          </NavNextLink>
        </>
      )}

      {!isPetsittingMode && hasRequestedPetsitting && (
        <>
          <NavNextLink
            active={
              activeItem === ActiveItem.Search ||
              activeItem === ActiveItem.Favorites ||
              activeItem === ActiveItem.Messages
            }
            href={appendQueryType(Routes.Search._pathname)}
          >
            <EmtIcon className="emt-paw-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
            <EmtTypography
              variant="body2"
              color="greyGradient.light"
              fontWeight="600"
              sx={{
                '& *': {
                  color:
                    activeItem === ActiveItem.Search ||
                    activeItem === ActiveItem.Favorites ||
                    activeItem === ActiveItem.Messages
                      ? '#4A4754!important'
                      : '#77757F!important',
                },
              }}
            >
              {t('navigation.mobile.emprunteurs')}
            </EmtTypography>
          </NavNextLink>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px 12px 0 0',
              color:
                activeItem === ActiveItem.PetSitterSearch ||
                activeItem === ActiveItem.PetSitterReservations ||
                activeItem === ActiveItem.PetSitterNewRequest ||
                activeItem === ActiveItem.PetSitterPayment
                  ? '#762D8A'
                  : 'none',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.preventDefault()
              const url = `https://app.supersitters.app${
                tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Base
              }?token=${encodeURIComponent(
                localStorage.getItem(TokenKeys.Auth ?? '') || '',
              )}&refreshToken=${encodeURIComponent(localStorage.getItem(TokenKeys.Refresh ?? '') || '')}&type=dogowner`
              openUrlApp(url)
            }}
          >
            {/* <NavNextLink
            active={
              activeItem === ActiveItem.PetSitterSearch ||
              activeItem === ActiveItem.PetSitterReservations ||
              activeItem === ActiveItem.PetSitterNewRequest ||
              activeItem === ActiveItem.PetSitterPayment
            }
            href={appendQueryType(tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Base)}
          > */}
            <EmtIcon
              className="emt-users-1"
              sx={{
                fontSize: '18px',
                color:
                  activeItem === ActiveItem.PetSitterSearch ||
                  activeItem === ActiveItem.PetSitterReservations ||
                  activeItem === ActiveItem.PetSitterNewRequest ||
                  activeItem === ActiveItem.PetSitterPayment
                    ? '#AE59C6!important'
                    : 'greyGradient.light',
              }}
            />
            <EmtTypography
              variant="body2"
              fontWeight="600"
              sx={{
                '& *': {
                  color:
                    activeItem === ActiveItem.PetSitterSearch ||
                    activeItem === ActiveItem.PetSitterReservations ||
                    activeItem === ActiveItem.PetSitterNewRequest ||
                    activeItem === ActiveItem.PetSitterPayment
                      ? '#4A4754!important'
                      : '#77757F!important',
                },
              }}
            >
              {t('navigation.mobile.petsitters')}
            </EmtTypography>
            {/* </NavNextLink> */}
          </Box>
        </>
      )}
      {isPetsittingMode && (
        <>
          <NavNextLink
            active={activeItem === ActiveItem.PetSitterSearch}
            href={appendQueryType(tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Search)}
          >
            <EmtIcon
              className="emt-search-1"
              sx={{
                fontSize: '18px',
                color: activeItem === ActiveItem.PetSitterSearch ? '#AE59C6!important' : 'greyGradient.light',
              }}
            />
            <EmtTypography
              variant="body2"
              fontWeight="600"
              sx={{
                '& *': {
                  color: activeItem === ActiveItem.PetSitterSearch ? '#4A4754!important' : '#77757F!important',
                },
              }}
            >
              {t('navigation.mobile.petsitter.search')}
            </EmtTypography>
          </NavNextLink>
          <NavNextLink
            active={activeItem === ActiveItem.PetSitterReservations}
            href={appendQueryType(
              tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Reservations.Base,
            )}
          >
            <EmtIcon
              className="emt-message-text-alt-1"
              sx={{
                fontSize: '18px',
                color: activeItem === ActiveItem.PetSitterReservations ? '#AE59C6!important' : 'greyGradient.light',
              }}
            />
            <EmtTypography
              variant="body2"
              fontWeight="600"
              sx={{
                '& *': {
                  color: activeItem === ActiveItem.PetSitterReservations ? '#4A4754!important' : '#77757F!important',
                },
              }}
            >
              {t('navigation.mobile.petsitter.reservations')}
            </EmtTypography>
          </NavNextLink>
        </>
      )}
    </>
  ) : (
    <>
      <NavNextLink
        active={activeItem === ActiveItem.Home}
        href={appendQueryType(Routes.Home)}
        sx={{ margin: '12px 0' }}
      >
        <EmtIcon className="emt-home-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.home')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink
        active={activeItem === ActiveItem.Messages}
        notification={!!messagesCount}
        href={appendQueryType(Routes.Message._pathname)}
      >
        <EmtIcon className="emt-message-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.messages')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink active={activeItem === ActiveItem.Search} href={appendQueryType(Routes.Search._pathname)}>
        <EmtIcon className="emt-search-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.search')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink
        active={activeItem === ActiveItem.Favorites}
        notification={!!favoritesCount}
        href={appendQueryType(Routes.Favorites._pathname, Routes.Favorites._params.Received)}
      >
        <EmtIcon className="emt-heart-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.favorites')}
        </EmtTypography>
      </NavNextLink>
    </>
  )

  return (
    <>
      <EmtNav
        drawerChildren={drawerChildren}
        drawerTitle={t('navigation.menu') as string}
        mobileChildren={mobileChildren}
        avatarUrl={isQueryOwner ? pets?.[0]?.mainPictureThumbnailUrl : currentUser?.mainPictureThumbnailUrl}
        avatarAlt={isQueryOwner ? pets?.[1]?.mainPictureThumbnailUrl : undefined}
        profilTitle={isQueryOwner ? (t('navigation.myPets') as string) : (t('navigation.myProfile') as string)}
        profilMenuChildren={profilMenuChildren}
        isQueryOwner={isQueryOwner}
        sx={{
          ...sx,
          '& .MuiPaper-rounded.MuiPopover-paper': {
            zIndex: 9999999999,
          },
        }}
        isDrawerOpen={isDrawerOpen}
        toggleDrawer={toggleDrawer}
        closeDrawer={closeDrawer}
        isProfileMenuOpen={isProfileMenuOpen}
        closeProfileMenu={closeProfileMenu}
        openProfileMenu={openProfileMenu}
        shouldTransform={shouldTransform}
      >
        {/* TO DO: uncomment */}
        {router.pathname === Routes.Home && isQueryOwner && !isPetsittingMode ? (
          <Box sx={{ width: 320, maxWidth: '100%' }}>
            <AppModeSelect appendQueryType={appendQueryType} />
          </Box>
        ) : (
          <NextALink href={appendQueryType(Routes.Home)}>
            <Box
              sx={{
                maxWidth: isPetsittingMode ? '250px' : '122px',
                objectFit: 'contain',
                '& img': { maxHeight: isPetsittingMode ? '65px' : '50px', objectFit: 'contain', width: '100%' },
              }}
            >
              <NextImage
                src={isPetsittingMode ? VariantImages.LogoSitterBlackFull : VariantImages.LogoPrimary}
                quality={100}
                alt={'Site logo'}
              />
            </Box>
          </NextALink>
        )}
        <NavNextLink active={activeItem === ActiveItem.Home} href={appendQueryType(Routes.Home)}>
          <EmtIcon className="emt-home-1" sx={{ fontSize: '16px' }} />
          <EmtTypography variant="body2" fontWeight="bold">
            {t('navigation.home')}
          </EmtTypography>
        </NavNextLink>
        {isQueryOwner ? (
          <>
            {!isPetsittingMode && !hasRequestedPetsitting && (
              <>
                <NavNextLink active={activeItem === ActiveItem.Search} href={appendQueryType(Routes.Search._pathname)}>
                  <EmtIcon className="emt-search-1" sx={{ fontSize: '16px' }} />
                  <EmtTypography
                    variant="body2"
                    fontWeight="700"
                    sx={{
                      '& *': {
                        color: activeItem === ActiveItem.Search ? '#4A4754!important' : '#77757F!important',
                      },
                    }}
                  >
                    {t('navigation.mobile.search')}
                  </EmtTypography>
                </NavNextLink>
                <NavNextLink
                  active={activeItem === ActiveItem.Messages}
                  href={appendQueryType(Routes.Message._pathname)}
                >
                  <EmtIcon className="emt-message-1" sx={{ fontSize: '16px' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px!important' }}>
                    <EmtTypography
                      variant="body2"
                      fontWeight="700"
                      sx={{
                        '& *': {
                          color: activeItem === ActiveItem.Messages ? '#4A4754!important' : '#77757F!important',
                        },
                      }}
                    >
                      {t('navigation.mobile.messages')}
                    </EmtTypography>
                    {!!messagesCount && <EmtChip color="primary" type="roundedNumber" label={messagesCount} />}
                  </Box>
                </NavNextLink>
                <NavNextLink
                  active={activeItem === ActiveItem.Favorites}
                  href={appendQueryType(Routes.Favorites._pathname)}
                >
                  <EmtIcon className="emt-heart-1" sx={{ fontSize: '16px' }} />
                  <EmtTypography
                    variant="body2"
                    fontWeight="700"
                    sx={{
                      '& *': {
                        color: activeItem === ActiveItem.Favorites ? '#4A4754!important' : '#77757F!important',
                      },
                    }}
                  >
                    {t('navigation.mobile.favorites')}
                  </EmtTypography>
                </NavNextLink>
              </>
            )}
            {!isPetsittingMode && hasRequestedPetsitting && (
              <>
                <NavNextLink
                  active={
                    activeItem === ActiveItem.Search ||
                    activeItem === ActiveItem.Messages ||
                    activeItem === ActiveItem.Favorites
                  }
                  href={appendQueryType(Routes.Search._pathname)}
                >
                  <EmtIcon className="emt-paw-1" sx={{ fontSize: '16px' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px!important' }}>
                    <EmtTypography variant="body2" fontWeight="bold" sx={{ marginTop: '2px' }}>
                      {t(`navigation.borrowers`)}
                    </EmtTypography>
                    {!!messagesCount && <EmtChip color="primary" type="roundedNumber" label={messagesCount} />}
                  </Box>
                </NavNextLink>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '12px 12px 0 0',
                    color:
                      activeItem === ActiveItem.PetSitterSearch ||
                      activeItem === ActiveItem.PetSitterReservations ||
                      activeItem === ActiveItem.PetSitterNewRequest ||
                      activeItem === ActiveItem.PetSitterPayment
                        ? '#762D8A'
                        : 'none',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    const url = `https://app.supersitters.app${
                      tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Base
                    }?token=${encodeURIComponent(
                      localStorage.getItem(TokenKeys.Auth ?? '') || '',
                    )}&refreshToken=${encodeURIComponent(
                      localStorage.getItem(TokenKeys.Refresh ?? '') || '',
                    )}&type=dogowner`
                    openUrlApp(url)
                  }}
                >
                  {/* <NavNextLink
                  active={
                    // activeItem === ActiveItem.PetSitter ||
                    activeItem === ActiveItem.PetSitterSearch ||
                    activeItem === ActiveItem.PetSitterReservations ||
                    activeItem === ActiveItem.PetSitterNewRequest ||
                    activeItem === ActiveItem.PetSitterPayment
                  }
                  href={appendQueryType(
                    tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Base,
                  )}
                > */}
                  <EmtIcon className="emt-users-1" sx={{ fontSize: '16px' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px!important' }}>
                    <EmtTypography variant="body2" fontWeight="bold" sx={{ marginTop: '2px' }}>
                      Pet
                    </EmtTypography>
                    <NextImage width={42} height={20} src={VariantImages.Sitter} alt="sitter" />
                  </Box>
                  {/* </NavNextLink> */}
                </Box>
              </>
            )}
            {isPetsittingMode && (
              <>
                <NavNextLink
                  active={activeItem === ActiveItem.PetSitterSearch}
                  href={appendQueryType(
                    tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Search,
                  )}
                >
                  <EmtIcon
                    className="emt-search-1"
                    sx={{
                      fontSize: '18px',
                      color: activeItem === ActiveItem.PetSitterSearch ? '#AE59C6!important' : 'greyGradient.light',
                    }}
                  />
                  <EmtTypography
                    variant="body2"
                    fontWeight="600"
                    sx={{
                      '& *': {
                        color: activeItem === ActiveItem.PetSitterSearch ? '#4A4754!important' : '#77757F!important',
                      },
                    }}
                  >
                    {t('navigation.mobile.petsitter.search')}
                  </EmtTypography>
                </NavNextLink>
                <NavNextLink
                  active={activeItem === ActiveItem.PetSitterReservations}
                  href={appendQueryType(
                    tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Reservations.Base,
                  )}
                >
                  <EmtIcon
                    className="emt-message-text-alt-1"
                    sx={{
                      fontSize: '18px',
                      color:
                        activeItem === ActiveItem.PetSitterReservations ? '#AE59C6!important' : 'greyGradient.light',
                    }}
                  />
                  <EmtTypography
                    variant="body2"
                    fontWeight="600"
                    sx={{
                      '& *': {
                        color:
                          activeItem === ActiveItem.PetSitterReservations ? '#4A4754!important' : '#77757F!important',
                      },
                    }}
                  >
                    {t('navigation.mobile.petsitter.reservations')}
                  </EmtTypography>
                </NavNextLink>
              </>
            )}
          </>
        ) : (
          <>
            <NavNextLink active={activeItem === ActiveItem.Search} href={appendQueryType(Routes.Search._pathname)}>
              <EmtIcon className="emt-search-1" sx={{ fontSize: '16px' }} />
              <EmtTypography variant="body2" fontWeight="bold">
                {t(`navigation.search.${queryType}`)}
              </EmtTypography>
            </NavNextLink>
            <NavNextLink active={activeItem === ActiveItem.Messages} href={appendQueryType(Routes.Message._pathname)}>
              <EmtIcon className="emt-message-1" sx={{ fontSize: '16px' }} />
              <EmtTypography variant="body2" fontWeight="bold">
                {t('navigation.messages')}
              </EmtTypography>
              {!!messagesCount && <EmtChip color="primary" type="roundedNumber" label={messagesCount} />}
            </NavNextLink>
            <NavNextLink
              active={activeItem === ActiveItem.Favorites}
              href={appendQueryType(Routes.Favorites._pathname, Routes.Favorites._params.Received)}
            >
              <EmtIcon className="emt-heart-1" sx={{ fontSize: '16px' }} />
              <EmtTypography variant="body2" fontWeight="bold">
                {t('navigation.favorites')}
              </EmtTypography>
              {!!favoritesCount && <EmtChip color="primary" type="roundedNumber" label={favoritesCount} />}
            </NavNextLink>
          </>
        )}
      </EmtNav>
      <AddAnimalModal
        open={isAddPetModalOpen}
        close={closeAddPetModal}
        defaultImages={isPetsittingMode}
        defaultDescription={isPetsittingMode}
      />
    </>
  )
}

const DefaultBrandNavigation = ({ sx }: NavigationProps) => {
  const { t } = useTranslation('common')
  const [isQueryOwner] = useIsQueryOwner()

  const {
    closeDrawer,
    isDrawerOpen,
    toggleDrawer,
    closeProfileMenu,
    openProfileMenu,
    isProfileMenuOpen,
    isAddPetModalOpen,
    closeAddPetModal,
  } = useUIStore()

  const { onLogoutClickAsync } = useLogout()

  const { isPetsittingMode } = usePetsittingMode()

  const {
    discussions: { unreadCount: discussionsUnreadCount },
    apiNotifications: { currentApiNotifications },
  } = useEMTState()
  const [, queryType, appendQueryType] = useIsQueryOwner(true)

  const { isPremium, currentUser, avatar, isCertified } = useCurrentUser()

  const messagesCount = discussionsUnreadCount
  const favoritesCount = currentApiNotifications.filter(
    (apiNotification) => apiNotification.status === 'unread' && apiNotification.context === 'favorite',
  ).length

  const router = useRouter()

  let activeItem: ActiveItem | null = null
  switch (router.pathname) {
    case Routes.Home:
      activeItem = ActiveItem.Home
      break
    case Routes.Search._pathname:
      activeItem = ActiveItem.Search
      break
    case Routes.Message._pathname:
      activeItem = ActiveItem.Messages
      break
    case Routes.Favorites._pathname:
      activeItem = ActiveItem.Favorites
      break
    default:
      break
  }

  const profilMenuChildren = isQueryOwner ? (
    <ProfileMenuPets
      pets={currentUser?.animals}
      onClick={() => {
        closeProfileMenu()
      }}
    />
  ) : (
    <>
      <Box
        onClick={() => {
          closeProfileMenu()
        }}
      >
        <NavNextLink key="edit-profile" href={appendQueryType(Routes.MyProfile)}>
          <EmtIcon className="emt-edit-1" sx={{ fontWeight: 'bold' }} />
          <EmtTypography variant="body2" fontWeight="bold">
            {t('navigation.editProfile')}
          </EmtTypography>
        </NavNextLink>
      </Box>

      <EmtNavLink
        key="logout"
        onClick={() => {
          closeProfileMenu()
          onLogoutClickAsync()
        }}
      >
        <EmtIcon className="emt-logout" sx={{ fontWeight: 'bold' }} />
        <EmtTypography variant="body2" fontWeight="bold">
          {t('navigation.logout')}
        </EmtTypography>
      </EmtNavLink>
    </>
  )

  const drawerChildren = (
    <>
      {isQueryOwner ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box>
            <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <EmtTypography variant="h5" fontWeight="normal">
                <Trans t={t} i18nKey="navigation.hello" values={{ name: currentUser?.displayName }} />
              </EmtTypography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                {isPremium && (
                  <EmtChip
                    label={t('profileCard.premium') as string}
                    color="secondarylight"
                    size="medium"
                    icon={<EmtIcon className="emt-crown" />}
                  />
                )}
                {isCertified && (
                  <EmtChip label={t('profileCard.idChecked') as string} color="successlight" size="medium" />
                )}
              </Box>
            </Box>
            <EmtTypography variant="body2">
              {isQueryOwner ? t('navigation.upToDateOwner') : t('navigation.upToDateBorrower')}
            </EmtTypography>
          </Box>
          {currentUser && (
            <PetsDropdown
              profile={currentUser}
              onPetClick={() => {
                closeDrawer()
              }}
            />
          )}
        </Box>
      ) : (
        <Box onClick={closeDrawer}>
          <NextMuiLink href={appendQueryType(Routes.MyProfile)} sx={{ textDecoration: 'none' }}>
            <EmtProfilBox
              username={currentUser?.displayName}
              secondaryLine={t('navigation.myProfile') as string}
              avatarUrl={currentUser?.mainPictureThumbnailUrl}
            />
          </NextMuiLink>
        </Box>
      )}
      <Box onClick={closeDrawer}>
        <BecomePremiumButton />
      </Box>
      <DrawerNextLink
        iconClassName="emt-cog"
        title={t('navigation.drawer.settings')}
        href={appendQueryType(Routes.MyAccount.settings)}
      />
      {SpecificModules.drawer.emergencyPetCareRelay && SpecificModules.others.emergencyPetCareRelay && (
        <DrawerNextLink
          icon={<EmtIcon fontSize="large" className="emt-pet-care-relay" />}
          title={t('navigation.drawer.emergencyPetCareRelay')}
          href={appendQueryType(Routes.EmergencyPetCareRelay)}
        />
      )}
      <DrawerNextLink
        iconClassName="emt-envelope"
        title={t('navigation.drawer.contact')}
        href={appendQueryType(Routes.Contact)}
      />

      {(!isQueryOwner && isPremium) || queryType === 'pro' ? (
        <DrawerNextLink
          icon={
            <Box sx={{ marginRight: '7px' }}>
              <CrossIcon color={colors.primary.main} />
            </Box>
          }
          title={t('navigation.drawer.assistance')}
          href={appendQueryType(Routes.Assistance)}
        />
      ) : null}
      {SpecificModules.drawer.partners && queryType !== 'pro' && (
        <DrawerNextLink
          iconClassName="emt-gift"
          title={t('navigation.drawer.partners')}
          href={appendQueryType(Routes.Partners)}
        />
      )}
      <DrawerNextLink
        iconClassName="emt-lock"
        title={t('navigation.drawer.safety')}
        href={appendQueryType(Routes.Safety)}
      />
      <DrawerNextLink
        iconClassName="emt-question-circle"
        title={t('navigation.drawer.help.title')}
        href={isPetsittingMode ? t('navigation.drawer.help.petsitting.url') : t('navigation.drawer.help.url')}
      />

      {!isPetsittingMode && (
        <Box onClick={closeDrawer}>
          <CertifyButton />
        </Box>
      )}

      <EmtSeparator />

      <Box
        onClick={() => {
          closeDrawer()
          onLogoutClickAsync()
        }}
      >
        <EmtDrawerLink sx={{ '& .emt-right-1': { display: 'none' } }}>
          <EmtIcon className="emt-logout" color="primary" />
          <EmtTypography variant="body1" color="black">
            {t('navigation.logout')}
          </EmtTypography>
        </EmtDrawerLink>
      </Box>
    </>
  )

  const mobileChildren = (
    <>
      <NavNextLink
        active={activeItem === ActiveItem.Home}
        href={appendQueryType(Routes.Home)}
        sx={{ margin: '12px 0' }}
      >
        <EmtIcon className="emt-home-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.home')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink
        active={activeItem === ActiveItem.Messages}
        notification={!!messagesCount}
        href={appendQueryType(Routes.Message._pathname)}
      >
        <EmtIcon className="emt-message-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.messages')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink active={activeItem === ActiveItem.Search} href={appendQueryType(Routes.Search._pathname)}>
        <EmtIcon className="emt-search-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.search')}
        </EmtTypography>
      </NavNextLink>
      <NavNextLink
        active={activeItem === ActiveItem.Favorites}
        notification={!!favoritesCount}
        href={appendQueryType(Routes.Favorites._pathname, Routes.Favorites._params.Received)}
      >
        <EmtIcon className="emt-heart-1" sx={{ fontSize: '18px', color: 'greyGradient.light' }} />
        <EmtTypography variant="body2" color="greyGradient.light">
          {t('navigation.mobile.favorites')}
        </EmtTypography>
      </NavNextLink>
    </>
  )

  return (
    <>
      <EmtNav
        drawerChildren={drawerChildren}
        drawerTitle={t('navigation.menu') as string}
        mobileChildren={mobileChildren}
        avatarUrl={avatar?.url}
        profilTitle={t('navigation.myProfile') as string}
        profilMenuChildren={profilMenuChildren}
        sx={{
          ...sx,
          '& .MuiPaper-rounded.MuiPopover-paper': {
            zIndex: 9999999999,
          },
        }}
        isDrawerOpen={isDrawerOpen}
        toggleDrawer={toggleDrawer}
        closeDrawer={closeDrawer}
        isProfileMenuOpen={isProfileMenuOpen}
        closeProfileMenu={closeProfileMenu}
        openProfileMenu={openProfileMenu}
      >
        <NextALink href={appendQueryType(Routes.Home)}>
          <Box
            sx={{
              maxWidth: '122px',
              objectFit: 'contain',
              '& img': { maxHeight: '50px', objectFit: 'contain', width: '100%' },
            }}
          >
            <NextImage src={VariantImages.LogoPrimary} quality={100} alt={'Site logo'} />
          </Box>
        </NextALink>
        <NavNextLink active={activeItem === ActiveItem.Home} href={appendQueryType(Routes.Home)}>
          <EmtIcon className="emt-home-1" sx={{ fontSize: '16px' }} />
          <EmtTypography variant="body2" fontWeight="bold">
            {t('navigation.home')}
          </EmtTypography>
        </NavNextLink>
        <NavNextLink active={activeItem === ActiveItem.Search} href={appendQueryType(Routes.Search._pathname)}>
          <EmtIcon className="emt-search-1" sx={{ fontSize: '16px' }} />
          <EmtTypography variant="body2" fontWeight="bold">
            {t(`navigation.search.${queryType}`)}
          </EmtTypography>
        </NavNextLink>
        <NavNextLink active={activeItem === ActiveItem.Messages} href={appendQueryType(Routes.Message._pathname)}>
          <EmtIcon className="emt-message-1" sx={{ fontSize: '16px' }} />
          <EmtTypography variant="body2" fontWeight="bold">
            {t('navigation.messages')}
          </EmtTypography>
          {!!messagesCount && <EmtChip color="primary" type="roundedNumber" label={messagesCount} />}
        </NavNextLink>
        <NavNextLink
          active={activeItem === ActiveItem.Favorites}
          href={appendQueryType(Routes.Favorites._pathname, Routes.Favorites._params.Received)}
        >
          <EmtIcon className="emt-heart-1" sx={{ fontSize: '16px' }} />
          <EmtTypography variant="body2" fontWeight="bold">
            {t('navigation.favorites')}
          </EmtTypography>
          {!!favoritesCount && <EmtChip color="primary" type="roundedNumber" label={favoritesCount} />}
        </NavNextLink>
      </EmtNav>
      <AddAnimalModal
        open={isAddPetModalOpen}
        close={closeAddPetModal}
        defaultImages={isPetsittingMode}
        defaultDescription={isPetsittingMode}
      />
    </>
  )
}
